import React, { useState } from 'react';
import { StyleSheet, View, Image, Platform, Keyboard, KeyboardAvoidingView } from 'react-native';
import { Button, TextInput, ErrorMessage } from '~/components/core';
import { useAppDispatch, useAppSelector, useStyles } from '~/hooks';
import { FONT_SIZE_S, FONT_SIZE_XS, FONT_SIZE_XXXL, FONT_SIZE_XXXXL, spacing } from '~/constants';
import { userAuthSelector } from '~/state/selectors';
import { loginAsCustomer } from '~/state/reducers/userAuthSlice';
import { scaleSize, scaleSizeMobile } from '~/constants/scale';
import { Theme } from '~/theme';
import config from '~/config';
import { appSettingsSelector } from '~/state/selectors';
import { SvgUri } from 'react-native-svg';
import { useBrowserLanguage } from '~/hooks/useBrowserLanguage';

export default function LoginAsCustomerForm() {
    const dispatch = useAppDispatch();
    const { errorMessage } = useAppSelector(userAuthSelector);
    const { colorScheme } = useAppSelector(appSettingsSelector);
    const [email, setEmail] = useState('');
    const [accountUsername, setAccountUsername] = useState('');
    const [password, setPassword] = useState('');
    const [passwordVisibility, setPasswordVisibility] = useState(true);
    const maxWidth: number = 600;
    const styles = useStyles(stylesGenerator, { maxWidth });
    useBrowserLanguage();

    function loginFunction() {
        dispatch(loginAsCustomer({ accountUsername, email, password }));
    }

    return (
        <KeyboardAvoidingView style={styles.container} behavior={'height'}>
            <View style={styles.logoContainer}>
                {Platform.OS === 'web' ? (
                    <Image
                        source={{uri: 'https://' +
                        config.EXTERNAL_SOURCES_URL +
                        'mobileplatform/images/brand/' +
                        config.BRAND_NAME +
                        '/home_logo_' +
                        colorScheme +
                        '.svg?1'}}
                        style={styles.logoStyle}
                        resizeMode="contain"
                    />
                ) : (
                    <SvgUri
                        width={'60%'}
                        style={styles.logoStyle}
                        uri={
                            'https://' +
                            config.EXTERNAL_SOURCES_URL +
                            'mobileplatform/images/brand/' +
                            config.BRAND_NAME +
                            '/home_logo_' +
                            colorScheme +
                            '.svg?1'
                        }
                    />
                )}
            </View>
            <TextInput
                containerStyle={styles.inputContainer}
                leftIcon="email"
                placeholder="CRM Username"
                autoCapitalize="none"
                keyboardType="email-address"
                textContentType="emailAddress"
                autoFocus={false}
                value={email}
                onChangeText={(text) => setEmail(text)}
                maxWidth={maxWidth}
                returnKeyType="next"
            />
            <TextInput
                containerStyle={styles.inputContainer}
                leftIcon="lock"
                placeholder="CRM Password"
                autoCapitalize="none"
                autoCorrect={false}
                secureTextEntry={passwordVisibility}
                textContentType="password"
                rightIcon={passwordVisibility ? 'eye' : 'eye-off'}
                value={password}
                onChangeText={(text) => setPassword(text)}
                onRightIconPress={() => setPasswordVisibility(!passwordVisibility)}
                maxWidth={maxWidth}
                returnKeyType="next"
            />
            <TextInput
                containerStyle={styles.inputContainer}
                leftIcon="email"
                placeholder="Client Username/email"
                autoCapitalize="none"
                keyboardType="email-address"
                textContentType="emailAddress"
                autoFocus={false}
                value={accountUsername}
                onChangeText={(text) => setAccountUsername(text)}
                maxWidth={maxWidth}
                returnKeyType="done"
                onSubmitEditing={() => {
                    if (email && password && email !== '' && password !== '' && accountUsername !== '') {
                        Keyboard.dismiss;
                        loginFunction();
                        Keyboard.dismiss;
                    }
                }}
            />
            <ErrorMessage text={errorMessage} visible={!!errorMessage} />
            <Button
                disabled={!email || !password}
                onPress={() => loginFunction()}
                title="auth-button-login"
                containerStyle={styles.loginButton}
                maxWidth={maxWidth}
            />
        </KeyboardAvoidingView>
    );
}

const stylesGenerator = (theme: Theme, custom?: { maxWidth: number }) =>
    StyleSheet.create({
        footerText: { color: theme.switch_trackOn, fontSize: FONT_SIZE_S },
        footerSeparator: {
            width: 1,
            height: scaleSize(48, custom?.maxWidth),
            backgroundColor: theme.background_buttonInactive,
        },
        footerContainer: { flexDirection: 'row', justifyContent: 'space-around', alignItems: 'center' },
        rememberMeText: { color: theme.tabIconSelected },
        rememberMeTextContainer: { paddingLeft: scaleSize(10, custom?.maxWidth) },
        brandNameText: { letterSpacing: 1, color: theme.text_symbol, fontSize: FONT_SIZE_XXXXL },
        brandNameSlogan: { letterSpacing: 1, fontSize: FONT_SIZE_XS, color: theme.switch_trackOn },
        logoStyle: { width: config.HOME_LOGO_SIZE_IN_PERCENT, height: '100%', maxWidth: scaleSizeMobile(custom?.maxWidth), maxHeight: scaleSizeMobile(custom?.maxWidth) },        logoContainer: { height: '50%', justifyContent: 'center', alignItems: 'center' },
        container: {
            flex: 1,
            justifyContent: 'space-between',
            alignSelf: 'center',
            maxWidth: custom?.maxWidth,
            paddingHorizontal: scaleSize(12, custom?.maxWidth),
            width: '100%',
            height: '100%',
            maxHeight: '100%',
        },
        title: {
            fontSize: FONT_SIZE_XXXL,
            fontWeight: '600',
            alignSelf: 'center',
            marginBottom: scaleSize(spacing.LARGE, custom?.maxWidth),
        },
        inputContainer: {
            marginBottom: scaleSize(spacing.LARGE, custom?.maxWidth),
        },
        switchContainer: {
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: scaleSize(spacing.LARGE, custom?.maxWidth),
        },
        loginButton: {
            marginBottom: scaleSize(spacing.LARGE, custom?.maxWidth),
        },
    });
